import React from 'react';
import { Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';

export interface TitleProps {
  children: React.ReactNode;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  color?: 'inherit' | 'primary' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error';
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  fontWeight?: number | string;
  gutterBottom?: boolean;
  noWrap?: boolean;
  sx?: TypographyProps['sx'];
}

export interface SubtitleProps {
  children: React.ReactNode;
  variant?: 'body1' | 'body2' | 'subtitle1' | 'subtitle2' | 'caption' | 'overline';
  color?: 'inherit' | 'primary' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error';
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  fontWeight?: number | string;
  gutterBottom?: boolean;
  noWrap?: boolean;
  sx?: TypographyProps['sx'];
}

/**
 * Title Component - Reusable Title Typography
 * 
 * A standardized title component that provides:
 * - Consistent heading styling across the application
 * - Flexible typography variants (h1-h6)
 * - Color and alignment options
 * - Responsive font sizing
 * - Custom styling support
 */
export const Title: React.FC<TitleProps> = ({
  children,
  variant = 'h4',
  color = 'textPrimary',
  align = 'inherit',
  fontWeight = 700,
  gutterBottom = false,
  noWrap = false,
  sx,
}) => {
  return (
    <Typography
      variant={variant}
      color={color}
      align={align}
      fontWeight={fontWeight}
      gutterBottom={gutterBottom}
      noWrap={noWrap}
      sx={{
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

/**
 * Subtitle Component - Reusable Subtitle Typography
 * 
 * A standardized subtitle component that provides:
 * - Consistent subtitle styling across the application
 * - Flexible typography variants (body1, body2, subtitle1, subtitle2, etc.)
 * - Color and alignment options
 * - Responsive font sizing
 * - Custom styling support
 */
export const Subtitle: React.FC<SubtitleProps> = ({
  children,
  variant = 'body2',
  color = 'textSecondary',
  align = 'inherit',
  fontWeight = 'normal',
  gutterBottom = false,
  noWrap = false,
  sx,
}) => {
  return (
    <Typography
      variant={variant}
      color={color}
      align={align}
      fontWeight={fontWeight}
      gutterBottom={gutterBottom}
      noWrap={noWrap}
      sx={{
        lineHeight: 1.5,
        opacity: 0.9,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

export default { Title, Subtitle };